import { z } from 'zod';

export enum JourneyStageId {
  Request = 'REQUEST',
  Response = 'RESPONSE',
  Validation = 'VALIDATION',
  Auction = 'AUCTION',
  Delivery = 'DELIVERY',
}

export enum JourneyNodeId {
  BidRequests = 'BID_REQUESTS',
  BidResponses = 'BID_RESPONSES',
  NoBids = 'NO_BIDS',
  BidTimeouts = 'BID_TIMEOUTS',
  ConnectionErrors = 'CONNECTION_ERRORS',
  DpHttpErrors = 'DP_HTTP_ERRORS',
  InvalidBids = 'INVALID_BIDS',
  ValidBids = 'VALID_BIDS',
  BidResponseErrors = 'BID_RESPONSE_ERRORS',
  UnwrapErrors = 'UNWRAP_ERRORS',
  BidWon = 'BID_WON',
  BidLost = 'BID_LOST',
  BidRejected = 'BID_REJECTED',
  Impressions = 'IMPRESSIONS',
  NoImpressions = 'NO_IMPRESSIONS',
  UndefinedRequest = 'UNDEFINED_REQUEST_OUTCOMES',
  UndefinedResponse = 'UNDEFINED_RESPONSE_OUTCOMES',
  UndefinedAuction = 'UNDEFINED_AUCTION_OUTCOMES',
  UndefinedDelivery = 'UNDEFINED_DELIVERY_OUTCOMES',
}

export enum JourneyTimeRange {
  FifteenMinutes = '15_MIN',
  ThirtyMinutes = '30_MIN',
  OneHour = '1_HOUR',
  FourHours = '4_HOURS',
  TwelveHours = '12_HOURS',
  TwentyFourHours = '24_HOURS',
}

export enum JourneyViewMode {
  CustomDate = 'CUSTOM_DATE',
  RealTime = 'REAL_TIME',
}

export enum JourneyNodeTone {
  Primary = 'PRIMARY',
  Success = 'SUCCESS',
  Warning = 'WARNING',
  Error = 'ERROR',
  Muted = 'MUTED',
  Undefined = 'UNDEFINED',
}

export enum JourneyEdgeType {
  Flow = 'FLOW',
  UnitTransition = 'UNIT_TRANSITION',
  Diagnostic = 'DIAGNOSTIC',
}
export enum JourneyNodeStatus {
  Observed = 'OBSERVED',
  Unavailable = 'UNAVAILABLE',
}
export enum JourneyCoverageStatus {
  Missing = 'MISSING',
  Partial = 'PARTIAL',
}
export enum JourneySliceStatus {
  Covered = 'COVERED',
  Missing = 'MISSING',
}
export enum JourneyUnit {
  Request = 'REQUEST',
  Bid = 'BID',
  Win = 'WIN',
  ErrorEvent = 'ERROR_EVENT',
}

const countSchema = z.number().int().nonnegative();
const breakdownSchema = z.object({
  id: z.string(),
  label: z.string(),
  count: countSchema,
  percentage: z.number().nullable(),
});
const nodeSchema = z.object({
  id: z.enum(JourneyNodeId),
  stage: z.enum(JourneyStageId),
  order: z.number().int(),
  label: z.string(),
  parentId: z.enum(JourneyNodeId).nullable(),
  edgeType: z.enum(JourneyEdgeType).nullable(),
  unit: z.enum(JourneyUnit),
  status: z.enum(JourneyNodeStatus),
  count: countSchema,
  percentOfParent: z.number().nullable(),
  tone: z.enum(JourneyNodeTone),
  description: z.string().optional(),
  availabilityNote: z.string().optional(),
  attributes: z
    .object({ attempts: countSchema, possibleDuplicateAttempts: countSchema })
    .optional(),
  subtypes: z.array(breakdownSchema).optional(),
  undefinedReasons: z.array(breakdownSchema).optional(),
});
export const journeyResponseSchema = z.object({
  apiVersion: z.literal('v2'),
  demandIntegrationId: z.uuid(),
  window: z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    granularity: z.literal('MINUTE'),
    timezone: z.literal('UTC'),
  }),
  coverage: z.object({
    status: z.enum(JourneyCoverageStatus),
    dataAsOf: z.iso.datetime().nullable(),
    dataAgeMinutes: z.number().int().nullable(),
    slices: z.array(
      z.object({
        from: z.iso.datetime(),
        to: z.iso.datetime(),
        status: z.enum(JourneySliceStatus),
      }),
    ),
    deliveryStatus: z.literal('PENDING'),
    note: z.string(),
    classificationVersion: z.number().int(),
    publicationVersion: z.number().int().nullable(),
  }),
  completeJourney: z.boolean(),
  stages: z.array(
    z.object({ id: z.enum(JourneyStageId), label: z.string(), order: z.number().int() }),
  ),
  nodes: z.array(nodeSchema),
});
export type JourneyResponse = z.infer<typeof journeyResponseSchema>;
export type JourneyNode = z.infer<typeof nodeSchema>;
export interface PositionedJourneyNode extends JourneyNode {
  x: number;
  y: number;
}
export interface JourneyRequest {
  publisherIds?: string[];
  placementSecondaryIds?: string[];
  demandIntegrationId: string;
  from: string;
  to: string;
}

export const journeyTimeRanges = [
  { id: JourneyTimeRange.FifteenMinutes, label: '15 min', minutes: 15 },
  { id: JourneyTimeRange.ThirtyMinutes, label: '30 min', minutes: 30 },
  { id: JourneyTimeRange.OneHour, label: '1 hr', minutes: 60 },
  { id: JourneyTimeRange.FourHours, label: '4 hrs', minutes: 240 },
  { id: JourneyTimeRange.TwelveHours, label: '12 hrs', minutes: 720 },
  { id: JourneyTimeRange.TwentyFourHours, label: '24 hrs', minutes: 1440 },
] as const;

export function journeyNodePercentage(node: JourneyNode): number | null {
  return node.status === JourneyNodeStatus.Observed && node.edgeType === JourneyEdgeType.Flow
    ? node.percentOfParent
    : null;
}

export function journeyRelatedNodeIds(
  nodes: readonly JourneyNode[],
  nodeId: JourneyNodeId | null,
): ReadonlySet<JourneyNodeId> {
  if (!nodeId) return new Set(nodes.map(({ id }) => id));
  const related = new Set<JourneyNodeId>([nodeId]);
  let ancestorId = nodes.find(({ id }) => id === nodeId)?.parentId;
  while (ancestorId && !related.has(ancestorId)) {
    related.add(ancestorId);
    ancestorId = nodes.find(({ id }) => id === ancestorId)?.parentId;
  }
  const descendants = [nodeId];
  while (descendants.length) {
    const parentId = descendants.shift();
    for (const child of nodes.filter((node) => node.parentId === parentId)) {
      if (related.has(child.id)) continue;
      related.add(child.id);
      descendants.push(child.id);
    }
  }
  return related;
}
